import * as React from 'react'

import {
  Badge,
  ChevronLeftIcon,
  ChevronRightIcon,
  CropIcon,
  getImageEditCssTransform,
  getImageAppearanceCssFilter,
  getRovingGridDestination,
  IconButton,
  Image as PopcornImage,
  ImageAppearanceFilter,
  isIdentityImageEdit,
  OctagonAlertIcon,
  PlayFilledIcon,
  Tooltip,
  UnstyledButton,
  useScroll,
  UserIcon,
  WarningIcon,
} from '@buffer-mono/popcorn'
import type { ImageEdit } from '@buffer-mono/popcorn'

import type {
  Image,
  Video,
} from '~publish/legacy/composer/composer/entities/factories'

import styles from './ComposerImageWorkspaceFilmstrip.module.css'

import type { JSX } from 'react'

export type ComposerImageWorkspaceFilmstripIssue = Readonly<{
  severity: 'warning' | 'error'
  label: string
}>

export type ComposerImageWorkspaceFilmstripImageItem = Readonly<{
  kind: 'image'
  image: Image
  thumbnailSrc: string
  thumbnailWidth: number
  thumbnailHeight: number
  edit: ImageEdit
  hasStagedChanges: boolean
  tagCount: number
  issue?: ComposerImageWorkspaceFilmstripIssue
}>

/**
 * Video is never editable through this workspace, so its filmstrip entry
 * carries only what it takes to display and select a thumbnail.
 */
export type ComposerImageWorkspaceFilmstripVideoItem = Readonly<{
  kind: 'video'
  video: Video
  thumbnailSrc: string | null
}>

export type ComposerImageWorkspaceFilmstripItem =
  | ComposerImageWorkspaceFilmstripImageItem
  | ComposerImageWorkspaceFilmstripVideoItem

export type ComposerImageWorkspaceFilmstripProps = Readonly<{
  items: readonly ComposerImageWorkspaceFilmstripItem[]
  activeIndex: number
  panelId: string
  disabled?: boolean
  focusRequest?: Readonly<{
    index: number
    key: number
  }>
  onSelect: (media: Image | Video) => void
}>

const NAVIGATION_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End'])
const FILMSTRIP_SCROLL_AMOUNT = 216

const getTagCountLabel = (tagCount: number): string => {
  if (tagCount === 1) return '1 tag'
  return `${tagCount} tags`
}

const getIssueLabel = (issue: ComposerImageWorkspaceFilmstripIssue): string =>
  `${issue.severity === 'error' ? 'Error' : 'Warning'}: ${issue.label}`

const getThumbnailLabel = (
  item: ComposerImageWorkspaceFilmstripItem,
  index: number,
  itemCount: number,
): string => {
  if (item.kind === 'video') return `Video ${index + 1} of ${itemCount}`

  return [
    `Image ${index + 1} of ${itemCount}`,
    item.hasStagedChanges ? 'edited' : undefined,
    item.tagCount > 0 ? getTagCountLabel(item.tagCount) : undefined,
    item.issue ? getIssueLabel(item.issue) : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(', ')
}

const getThumbnailStatusSummary = (
  item: ComposerImageWorkspaceFilmstripItem,
): string => {
  if (item.kind === 'video') return ''

  return [
    item.issue ? getIssueLabel(item.issue) : undefined,
    !isIdentityImageEdit(item.edit)
      ? 'Image edits changed'
      : item.hasStagedChanges
        ? 'Changes not yet applied'
        : undefined,
    item.tagCount > 0 ? getTagCountLabel(item.tagCount) : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(' · ')
}

/** The underlying media reference an item selects — used for identity checks. */
const getItemMedia = (
  item: ComposerImageWorkspaceFilmstripItem,
): Image | Video => (item.kind === 'image' ? item.image : item.video)

export const getImageWorkspaceFilmstripTabId = (
  panelId: string,
  index: number,
): string => `${panelId}-tab-${index}`

const getThumbnailAppearanceFilterId = (
  panelId: string,
  index: number,
): string => `${panelId}-tab-${index}-appearance-filter`

const getThumbnailPreviewStyles = (
  item: ComposerImageWorkspaceFilmstripImageItem,
  appearanceFilterId: string,
): Readonly<{
  crop: React.CSSProperties
  image: React.CSSProperties
  orientedImage: React.CSSProperties
}> => {
  const { crop, rotation } = item.edit
  const isQuarterTurn = rotation === 90 || rotation === 270
  const orientedWidth = isQuarterTurn
    ? item.thumbnailHeight
    : item.thumbnailWidth
  const orientedHeight = isQuarterTurn
    ? item.thumbnailWidth
    : item.thumbnailHeight
  const cropAspectRatio =
    (orientedWidth * crop.width) / (orientedHeight * crop.height)
  const sourceAspectRatio = item.thumbnailWidth / item.thumbnailHeight

  return {
    crop:
      cropAspectRatio >= 1
        ? { width: '100%', height: `${100 / cropAspectRatio}%` }
        : { width: `${cropAspectRatio * 100}%`, height: '100%' },
    orientedImage: {
      left: `${(-crop.x / crop.width) * 100}%`,
      top: `${(-crop.y / crop.height) * 100}%`,
      width: `${100 / crop.width}%`,
      height: `${100 / crop.height}%`,
    },
    image: {
      width: isQuarterTurn ? `${sourceAspectRatio * 100}%` : '100%',
      height: isQuarterTurn ? `${(1 / sourceAspectRatio) * 100}%` : '100%',
      transform: getImageEditCssTransform(
        { width: item.thumbnailWidth, height: item.thumbnailHeight },
        item.edit,
      ),
      filter: getImageAppearanceCssFilter(item.edit, appearanceFilterId),
    },
  }
}

const scrollThumbnailIntoView = (
  list: HTMLUListElement | null,
  button: HTMLButtonElement | null,
): void => {
  if (!list || !button) return

  const listRect = list.getBoundingClientRect()
  const buttonRect = button.getBoundingClientRect()
  const leftOverflow = buttonRect.left - listRect.left
  const rightOverflow = buttonRect.right - listRect.right
  const scrollDelta =
    leftOverflow < 0 ? leftOverflow : rightOverflow > 0 ? rightOverflow : 0

  if (scrollDelta === 0) return

  if (typeof list.scrollBy === 'function') {
    list.scrollBy({ left: scrollDelta, behavior: 'auto' })
    return
  }

  list.scrollLeft += scrollDelta
}

export const ComposerImageWorkspaceFilmstrip = ({
  items,
  activeIndex,
  panelId,
  disabled = false,
  focusRequest,
  onSelect,
}: ComposerImageWorkspaceFilmstripProps): JSX.Element => {
  const listRef = React.useRef<HTMLUListElement>(null)
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([])
  const pointerDownMediaRef = React.useRef<Image | Video | null>(null)
  const selectionRequestedFromPointerFocusRef = React.useRef<
    Image | Video | null
  >(null)
  const focusRequestIndex = focusRequest?.index
  const focusRequestKey = focusRequest?.key
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useScroll(
    listRef,
    {
      direction: 'horizontal',
      scrollAmount: FILMSTRIP_SCROLL_AMOUNT,
    },
  )
  const hasOverflow = canScrollLeft || canScrollRight

  React.useEffect(() => {
    scrollThumbnailIntoView(
      listRef.current,
      buttonRefs.current[activeIndex] ?? null,
    )
  }, [activeIndex])

  React.useEffect(() => {
    if (focusRequestIndex === undefined) return

    const button = buttonRefs.current[focusRequestIndex]
    if (!button) return

    scrollThumbnailIntoView(listRef.current, button)
    button.focus()
  }, [focusRequestIndex, focusRequestKey])

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    if (!NAVIGATION_KEYS.has(event.key)) return

    event.preventDefault()
    const destinationIndex = getRovingGridDestination(event.key, index, {
      itemCount: items.length,
      columns: items.length,
    })
    if (destinationIndex === index) return

    const destinationButton = buttonRefs.current[destinationIndex]
    if (!destinationButton) return

    destinationButton.focus()
  }

  return (
    <div className={styles.filmstrip}>
      <div
        className={styles.scroller}
        data-has-overflow={hasOverflow ? 'true' : undefined}
      >
        <ul
          ref={listRef}
          className={styles.list}
          role="tablist"
          aria-label="Attached media"
          aria-orientation="horizontal"
        >
          {items.map((item, index) => {
            const isCurrent = index === activeIndex
            const statusSummary = getThumbnailStatusSummary(item)
            const appearanceFilterId = getThumbnailAppearanceFilterId(
              panelId,
              index,
            )
            const media = getItemMedia(item)
            const hasImageEdit =
              item.kind === 'image' && !isIdentityImageEdit(item.edit)
            const thumbnailPreview =
              item.kind === 'image' ? (
                ((): JSX.Element => {
                  const thumbnailStyles = getThumbnailPreviewStyles(
                    item,
                    appearanceFilterId,
                  )
                  return (
                    <span
                      className={styles.thumbnailPreview}
                      aria-hidden="true"
                    >
                      <span
                        className={styles.thumbnailCrop}
                        style={thumbnailStyles.crop}
                      >
                        <span
                          className={styles.thumbnailOrientedImage}
                          style={thumbnailStyles.orientedImage}
                        >
                          <PopcornImage
                            className={styles.thumbnail}
                            style={thumbnailStyles.image}
                            src={item.thumbnailSrc}
                            alt=""
                            draggable={false}
                          />
                        </span>
                      </span>
                      <ImageAppearanceFilter
                        id={appearanceFilterId}
                        appearance={item.edit}
                      />
                    </span>
                  )
                })()
              ) : (
                <span className={styles.thumbnailPreview} aria-hidden="true">
                  {item.thumbnailSrc ? (
                    <PopcornImage
                      className={styles.thumbnail}
                      src={item.thumbnailSrc}
                      alt=""
                      draggable={false}
                    />
                  ) : null}
                  <Badge
                    className={styles.videoIndicator}
                    size="small"
                    variant="light"
                  >
                    <PlayFilledIcon size="small" />
                  </Badge>
                </span>
              )
            const thumbnailButton = (
              <UnstyledButton
                ref={(button) => {
                  buttonRefs.current[index] = button
                }}
                id={getImageWorkspaceFilmstripTabId(panelId, index)}
                type="button"
                role="tab"
                className={styles.thumbnailButton}
                aria-label={getThumbnailLabel(item, index, items.length)}
                aria-controls={panelId}
                aria-selected={isCurrent}
                tabIndex={!disabled && isCurrent ? 0 : -1}
                disabled={disabled}
                onPointerDown={(event) => {
                  if (event.button !== 0) return

                  selectionRequestedFromPointerFocusRef.current = null
                  pointerDownMediaRef.current = media
                }}
                onPointerCancel={() => {
                  pointerDownMediaRef.current = null
                  selectionRequestedFromPointerFocusRef.current = null
                }}
                onFocus={() => {
                  if (!isCurrent) {
                    if (pointerDownMediaRef.current === media) {
                      selectionRequestedFromPointerFocusRef.current = media
                    }
                    onSelect(media)
                  }
                }}
                onBlur={() => {
                  pointerDownMediaRef.current = null
                  selectionRequestedFromPointerFocusRef.current = null
                }}
                onClick={() => {
                  pointerDownMediaRef.current = null
                  if (selectionRequestedFromPointerFocusRef.current === media) {
                    selectionRequestedFromPointerFocusRef.current = null
                    return
                  }
                  if (!isCurrent) onSelect(media)
                }}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                {thumbnailPreview}
                {item.kind === 'image' && item.issue ? (
                  <Badge
                    className={styles.issueIndicator}
                    data-severity={item.issue.severity}
                    size="small"
                    variant={
                      item.issue.severity === 'warning' ? 'warning' : 'light'
                    }
                    aria-hidden="true"
                  >
                    {item.issue.severity === 'warning' ? (
                      <WarningIcon size="small" />
                    ) : (
                      <OctagonAlertIcon size="small" />
                    )}
                  </Badge>
                ) : null}
                {item.kind === 'image' &&
                (hasImageEdit || item.tagCount > 0) ? (
                  <span className={styles.statusRow} aria-hidden="true">
                    {hasImageEdit ? (
                      <Badge
                        className={styles.imageEditIndicator}
                        size="small"
                        variant="light"
                      >
                        <CropIcon size="small" />
                      </Badge>
                    ) : null}
                    {item.tagCount > 0 ? (
                      <Badge size="small" variant="light">
                        <UserIcon size="small" />
                      </Badge>
                    ) : null}
                  </span>
                ) : null}
              </UnstyledButton>
            )

            return (
              // The workspace snapshot never reorders, and legacy images have no
              // stable identifier beyond their object reference.
              // eslint-disable-next-line react/no-array-index-key
              <li key={index} className={styles.item} role="presentation">
                {statusSummary ? (
                  <Tooltip content={statusSummary} side="bottom">
                    {thumbnailButton}
                  </Tooltip>
                ) : (
                  thumbnailButton
                )}
              </li>
            )
          })}
        </ul>

        {!disabled && hasOverflow ? (
          <>
            <IconButton
              className={`${styles.scrollButton} ${styles.scrollButtonLeft}`}
              label="Scroll thumbnails left"
              size="small"
              variant="secondary"
              aria-disabled={!canScrollLeft}
              onClick={canScrollLeft ? scrollLeft : undefined}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              className={`${styles.scrollButton} ${styles.scrollButtonRight}`}
              label="Scroll thumbnails right"
              size="small"
              variant="secondary"
              aria-disabled={!canScrollRight}
              onClick={canScrollRight ? scrollRight : undefined}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        ) : null}
      </div>
    </div>
  )
}
